import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as LabelPrimitive from '@radix-ui/react-label';
import { forwardRef, useId, } from 'react';
import { cn } from "../lib/cn.js";
export const Input = forwardRef(({ className, invalid = false, ...props }, ref) => (_jsx("input", { ref: ref, "aria-invalid": invalid || undefined, className: cn('h-(--control-height-md) w-full rounded-(--radius-md) px-2.5', 'bg-(--surface) text-(--text-primary) text-(length:--text-sm)', 'border border-(--border-default)', 'placeholder:text-(--text-muted)', 'transition-colors duration-(--duration-fast)', 'hover:border-(--border-strong)', 'disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-(--surface-sunken)', 'aria-[invalid]:border-(--danger)', className), ...props })));
Input.displayName = 'Input';
/**
 * Shared with Input so a form does not look assembled from two kits.
 *
 * Exported (not just module-local) so a field that cannot be a plain
 * `<input>` — `DateField`'s native date/time inputs, for instance — can wear
 * the exact same surface instead of a hand-tuned lookalike that drifts the
 * next time this one changes.
 */
export const CONTROL_SURFACE = [
    'w-full rounded-(--radius-md) px-2.5',
    'bg-(--surface) text-(--text-primary) text-(length:--text-sm)',
    'border border-(--border-default)',
    'placeholder:text-(--text-muted)',
    'transition-colors duration-(--duration-fast)',
    'hover:border-(--border-strong)',
    'disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-(--surface-sunken)',
    'aria-[invalid]:border-(--danger)',
];
/**
 * A native `<select>`.
 *
 * Not a Radix listbox, deliberately. A native select gets the platform's own
 * keyboard behaviour for free — type-ahead, Home/End, and on a phone the OS
 * picker — which is what an operator entering a hundred rows a day actually
 * relies on. A custom listbox would have to reimplement all of it to break
 * even, and adds a dependency plus a portal to a form that has no need of one.
 */
export const Select = forwardRef(({ className, invalid = false, ...props }, ref) => (_jsx("select", { ref: ref, "aria-invalid": invalid || undefined, className: cn(...CONTROL_SURFACE, 'h-(--control-height-md)', 
    // Room for the platform's own arrow, which varies by browser.
    'pr-8', className), ...props })));
Select.displayName = 'Select';
export const Textarea = forwardRef(({ className, invalid = false, rows = 3, ...props }, ref) => (_jsx("textarea", { ref: ref, rows: rows, "aria-invalid": invalid || undefined, className: cn(...CONTROL_SURFACE, 'py-2 leading-(--leading-normal)', className), ...props })));
Textarea.displayName = 'Textarea';
export const Label = forwardRef(({ className, required = false, children, ...props }, ref) => (_jsxs(LabelPrimitive.Root, { ref: ref, className: cn('text-(length:--text-xs) font-(--weight-medium) text-(--text-secondary)', 'select-none', className), ...props, children: [children, required ? (_jsx("span", { "aria-hidden": "true", className: "ml-0.5 text-(--danger)", children: "*" })) : null] })));
Label.displayName = 'Label';
/**
 * Label, control and message wired together.
 *
 * The control is a render prop so the association is not optional: the field
 * hands down the id and aria-describedby rather than trusting each caller to
 * remember them. An unlabelled input in an ERP form is a daily obstacle for
 * keyboard and screen-reader users, not an edge case.
 *
 * Error text replaces hint text rather than stacking, so the message that
 * matters is the one being announced.
 */
export const Field = ({ label, children, error, hint, required = false, className, help, }) => {
    const id = useId();
    const messageId = `${id}-message`;
    const helpId = `${id}-help`;
    const message = error ?? hint;
    const describedBy = [message ? messageId : null, help ? helpId : null]
        .filter((value) => value !== null)
        .join(' ') || undefined;
    return (_jsxs("div", { className: cn('flex flex-col gap-1', className), children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx(Label, { htmlFor: id, required: required, children: label }), help ? help({ id: helpId }) : null] }), children({
                id,
                'aria-describedby': describedBy,
                invalid: error !== undefined,
            }), message ? (_jsx("p", { id: messageId, 
                // Errors are announced; hints are not, so a screen reader is not
                // interrupted by static guidance.
                role: error ? 'alert' : undefined, className: cn('text-(length:--text-xs)', error ? 'text-(--danger)' : 'text-(--text-muted)'), children: message })) : null] }));
};
//# sourceMappingURL=input.js.map